import type { intelligence } from '@cwos/api-client';
import {
  comparisonView,
  formatMeasure,
  measureState,
  subjectLine,
  withheldSentence,
  ABSENT_SENTENCE,
} from './format.js';
import { kpiEvidenceLines } from './evidence.js';

/**
 * The headline number an operational question asks for.
 *
 * Shape, in the order a reader takes it in:
 *
 *     Total hours worked          ← what is being measured
 *     1,284.5 hours               ← the value, with its unit and its precision
 *     Last week · 33 workers      ← the RESOLVED period and who stands behind it
 *     ↑ 6.2% vs previous week     ← only when the server sent a real delta
 *
 * Everything above comes from the typed payload
 * (`analyticsQueryOutput` → {@link intelligence.AnalyticsKpi}). Nothing is
 * parsed from the answer prose, and the value is never re-derived: the
 * precision the executor declared is the precision rendered, so a metric
 * reported to one decimal place is not silently rounded to a rounder-looking
 * story.
 *
 * THE TWO NULLS. `value: null` is not one state. Suppressed-for-small-N is a
 * real figure withheld from this caller; not-recorded is the sources having
 * nothing. Both render as words rather than a number, and NEITHER renders as
 * `0` — see `format.ts` `measureState`, which is the only place that decision
 * is made.
 *
 * THE ARROW IS NOT A VERDICT. A rise in hours worked is good news and a rise in
 * missed shifts is bad news, and the payload does not say which this is. So the
 * delta carries direction and magnitude in the ordinary ink of the page, with
 * no green and no red — the component does not have an opinion to colour.
 *
 * Absent payload → renders nothing at all. Not a skeleton: a skeleton is a
 * promise that data is coming, and an answer that carried no KPI is not late.
 */
export function KpiResult({ kpi }: { kpi: intelligence.AnalyticsKpi | null | undefined }) {
  if (kpi === null || kpi === undefined) return null;

  const state = measureState(kpi.value, kpi.disclosure, 'value');
  const delta = comparisonView(kpi);

  return (
    <section className="rn-card" aria-label={kpi.label}>
      <p
        className="rn-muted"
        style={{ margin: 0, textTransform: 'uppercase', fontSize: 11, letterSpacing: '0.04em' }}
      >
        {kpi.label}
      </p>

      {state.kind === 'value' ? (
        <p
          style={{
            margin: 'var(--space-6) 0 0',
            fontFamily: 'var(--font-display)',
            fontWeight: 700,
            fontSize: 34,
            lineHeight: 1.1,
            letterSpacing: '-0.02em',
          }}
        >
          {formatMeasure(state.value, kpi.unit, kpi.precision)}
        </p>
      ) : (
        <>
          <p
            style={{
              margin: 'var(--space-6) 0 0',
              fontFamily: 'var(--font-display)',
              fontWeight: 700,
              fontSize: 22,
              lineHeight: 1.2,
              color: 'var(--color-ink2)',
            }}
          >
            {state.kind === 'withheld' ? 'Withheld' : 'Not recorded'}
          </p>
          <p className="rn-muted" style={{ margin: 'var(--space-6) 0 0' }}>
            {state.kind === 'withheld' ? withheldSentence(state) : ABSENT_SENTENCE}
          </p>
        </>
      )}

      <p className="rn-muted" style={{ margin: 'var(--space-6) 0 0' }}>
        {subjectLine(kpi)}
      </p>

      {delta !== null ? (
        <p
          style={{
            margin: 'var(--space-10) 0 0',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 'var(--space-6)',
            fontWeight: 600,
            fontSize: 13,
          }}
        >
          {/* The glyph is decoration; the WORD carries the direction, because
              "↑" has no reliable spoken form and a delta whose sign lives only
              in the glyph is announced as a bare magnitude. */}
          <span aria-hidden="true">{delta.arrow}</span>
          <span>{delta.text}</span>
        </p>
      ) : null}

      <KpiEvidence kpi={kpi} />
    </section>
  );
}

/**
 * What a figure is based on, in the words the question was asked in.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * WHAT THIS BLOCK USED TO SAY (F235, live on staging).
 *
 *     Evidence — 3 classes
 *       OPERATIONAL   412 records — 412 time_session
 *       CATALOG       metric: hours_worked, computed by: dashboard analytics
 *                     engine (hours_worked)
 *       SUPPORTING    analytics.query_metric@1
 *
 * "Class" is how the analytics envelope describes its own partitions;
 * `time_session` is a table; the catalog strings are keys with prose in front
 * of them; the supporting ref is a manifest correlation id in a notation that
 * no longer addresses anything. The owner named this class of leak by name, and
 * this card was the last of it on the screen — the conversation AROUND it had
 * already been translated, and the sweep that guards the rest of the surface
 * had to cut a hole around this subtree to stay green.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * WHAT IT SAYS NOW.
 *
 *     What this figure is based on
 *       METRIC DEFINITIONS   Hours worked
 *       RECORDS              412 time records
 *       PERIOD               Aug 17–23
 *
 * The classes are still rendered SEPARATELY, and for the original reason: an
 * answer with operational counts and no supporting refs is a real and honest
 * state — computed, its detail not retained — and flattening the classes would
 * turn that into the blunt claim "no evidence". Only the vocabulary changed.
 *
 * The translation lives in `evidence.ts`, which imports the conversation
 * surface's own tables (`../evidenceView.js`) rather than keeping a second
 * copy — a resource type must not become one word here and another word six
 * pixels away.
 *
 * Counts and translated nouns only. Never rows, never a key.
 */
function KpiEvidence({ kpi }: { kpi: intelligence.AnalyticsKpi }) {
  const lines = kpiEvidenceLines(kpi);

  if (lines.length === 0) {
    return (
      <p className="rn-muted" style={{ margin: 'var(--space-10) 0 0' }}>
        No records were consulted for this figure.
      </p>
    );
  }

  return (
    <details style={{ marginTop: 'var(--space-10)' }}>
      <summary className="rn-link" style={{ cursor: 'pointer' }}>
        What this figure is based on
      </summary>
      <dl style={{ margin: 'var(--space-8) 0 0' }}>
        {lines.map((line) => (
          <div key={line.key} style={{ marginBottom: 'var(--space-6)' }}>
            <dt
              className="rn-muted"
              style={{ textTransform: 'uppercase', fontSize: 11, letterSpacing: '0.04em' }}
            >
              {line.eyebrow}
            </dt>
            <dd className="rn-muted" style={{ margin: 0 }}>
              {line.text}
            </dd>
          </div>
        ))}
      </dl>
    </details>
  );
}
